import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandshake, faFile, faListCheck, faUsers, faDownload,
  faShieldHalved, faCircleCheck, faClock, faSpinner,
  faTriangleExclamation, faLock,
} from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';
import { Badge } from '../components/common/Badge';
import { Skeleton } from '../components/common/Skeleton';
import { dealRooms, documents as docsApi } from '../api';
import type { DealRoom, Task } from '../types';

const STATUS_ICON: Record<string, typeof faClock> = { pending: faClock, in_progress: faSpinner, completed: faCircleCheck, delayed: faTriangleExclamation };
const STATUS_COLOR: Record<string, string> = { pending: 'var(--color-text-tertiary)', in_progress: 'var(--color-info)', completed: 'var(--color-success)', delayed: 'var(--color-error)' };
const STATUS_LABEL: Record<string, string> = { pending: '대기', in_progress: '진행 중', completed: '완료', delayed: '지연' };

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

interface PublicDoc { id: number; name: string; file_type: string; file_size: number; uploaded_at: string; }
interface PublicMember { name: string; role: string; side: string; }

export function PublicRoom() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [room, setRoom] = useState<DealRoom | null>(null);
  const [dealName, setDealName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [docs, setDocs] = useState<PublicDoc[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [members, setMembers] = useState<PublicMember[]>([]);
  const [ndaAccepted, setNdaAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    dealRooms.getPublic(token)
      .then(data => {
        const r = data.room as DealRoom & { deal_name?: string; company_name?: string };
        setRoom(r);
        setDealName(r.deal_name || '');
        setCompanyName(r.company_name || '');
        setDocs(data.documents as unknown as PublicDoc[]);
        setTasks(data.tasks as unknown as Task[]);
        setMembers(data.members as unknown as PublicMember[]);
      })
      .catch(() => setError('딜룸을 찾을 수 없거나 만료되었습니다'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <main id="main-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ maxWidth: '600px', width: '100%', padding: 'var(--space-4)' }}>
        <Skeleton height="2rem" width="200px" style={{ marginBottom: 'var(--space-4)' }} />
        <Skeleton height="1rem" width="300px" style={{ marginBottom: 'var(--space-8)' }} />
        <Skeleton height="120px" borderRadius="var(--radius-xl)" style={{ marginBottom: 'var(--space-4)' }} />
        <Skeleton height="120px" borderRadius="var(--radius-xl)" />
      </div>
    </main>
  );

  if (error) return (
    <main id="main-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 'var(--space-4)' }}>
      <div>
        <FontAwesomeIcon icon={faLock} style={{ fontSize: 'var(--text-4xl)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-4)' }} />
        <h1 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--space-2)' }}>접근할 수 없습니다</h1>
        <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
      </div>
    </main>
  );

  // NDA Gate
  if (room?.require_nda && !ndaAccepted) return (
    <main id="main-content" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)' }}>
      <FadeUp>
        <GlassCard style={{ maxWidth: '480px', width: '100%', textAlign: 'center', padding: 'var(--space-10)' }}>
          <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: 'var(--text-4xl)', color: 'var(--color-accent)', marginBottom: 'var(--space-4)' }} />
          <h1 style={{ fontSize: 'var(--text-2xl)', marginBottom: 'var(--space-3)' }}>비밀유지계약(NDA)</h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-6)' }}>
            이 딜룸의 문서를 열람하려면 비밀유지계약에 동의해야 합니다.
            공유된 모든 정보는 기밀이며 제3자에게 공개할 수 없습니다.
          </p>
          <Button variant="primary" size="lg" onClick={() => setNdaAccepted(true)} style={{ width: '100%', justifyContent: 'center' }}>
            동의하고 계속하기
          </Button>
        </GlassCard>
      </FadeUp>
    </main>
  );

  const completedTasks = tasks.filter(t => t.status === 'completed').length;
  const progress = tasks.length ? Math.round((completedTasks / tasks.length) * 100) : 0;
  const tasksBySection = tasks.reduce((acc, t) => { (acc[t.section_name] ??= []).push(t); return acc; }, {} as Record<string, Task[]>);

  return (
    <main id="main-content" role="main" style={{ minHeight: '100vh', padding: 'var(--space-8) var(--space-4)', maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <FadeUp>
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-10)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
            <FontAwesomeIcon icon={faHandshake} style={{ color: 'var(--color-accent)', fontSize: 'var(--text-xl)' }} />
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--text-lg)' }}>딜룸</span>
          </div>
          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>{dealName}</h1>
          {companyName && <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-lg)' }}>{companyName}</p>}
        </div>
      </FadeUp>

      {/* Progress */}
      <FadeUp delay={0.05}>
        <GlassCard style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }}>진행률</div>
          <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 800, fontFamily: 'var(--font-display)', marginBottom: 'var(--space-3)' }}>{progress}%</div>
          <div style={{ height: '6px', borderRadius: 'var(--radius-full)', background: 'var(--color-bg-secondary)', overflow: 'hidden', maxWidth: '300px', margin: '0 auto' }}>
            <div style={{ height: '100%', width: `${progress}%`, borderRadius: 'var(--radius-full)', background: 'var(--color-accent)', transition: 'width 1s var(--ease-out-expo)' }} />
          </div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-2)' }}>{completedTasks} / {tasks.length} 태스크 완료</div>
        </GlassCard>
      </FadeUp>

      {/* Tasks */}
      {Object.keys(tasksBySection).length > 0 && (
        <FadeUp delay={0.1}>
          <h2 style={{ fontSize: 'var(--text-xl)', marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <FontAwesomeIcon icon={faListCheck} style={{ color: 'var(--color-accent)' }} /> 진행 상황
          </h2>
          {Object.entries(tasksBySection).map(([section, sectionTasks]) => (
            <div key={section} style={{ marginBottom: 'var(--space-6)' }}>
              <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-3)' }}>{section}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {sectionTasks.map(task => (
                  <GlassCard key={task.id} style={{ padding: 'var(--space-3) var(--space-4)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <FontAwesomeIcon icon={STATUS_ICON[task.status] || faClock} style={{ color: STATUS_COLOR[task.status] || 'var(--color-text-tertiary)', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontWeight: 500, textDecoration: task.status === 'completed' ? 'line-through' : 'none', opacity: task.status === 'completed' ? 0.6 : 1 }}>{task.title}</span>
                      <Badge color={STATUS_COLOR[task.status]}>{STATUS_LABEL[task.status] || task.status}</Badge>
                    </div>
                  </GlassCard>
                ))}
              </div>
            </div>
          ))}
        </FadeUp>
      )}

      {/* Documents */}
      {docs.length > 0 && (
        <FadeUp delay={0.15}>
          <h2 style={{ fontSize: 'var(--text-xl)', marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <FontAwesomeIcon icon={faFile} style={{ color: 'var(--color-accent)' }} /> 공유 문서
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-8)' }}>
            {docs.map(doc => (
              <GlassCard key={doc.id} style={{ padding: 'var(--space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <FontAwesomeIcon icon={faFile} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.name}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                      {doc.file_type} &middot; {formatSize(doc.file_size)}
                    </div>
                  </div>
                  {room?.allow_download ? (
                    <a href={docsApi.downloadUrl(doc.id)} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" aria-label={`${doc.name} 다운로드`}>
                        <FontAwesomeIcon icon={faDownload} />
                      </Button>
                    </a>
                  ) : (
                    <Badge color="var(--color-text-tertiary)">열람 전용</Badge>
                  )}
                </div>
              </GlassCard>
            ))}
          </div>
        </FadeUp>
      )}

      {/* Team */}
      {members.length > 0 && (
        <FadeUp delay={0.2}>
          <h2 style={{ fontSize: 'var(--text-xl)', marginBottom: 'var(--space-4)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <FontAwesomeIcon icon={faUsers} style={{ color: 'var(--color-accent)' }} /> 참여 멤버
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-8)' }}>
            {members.map((m, i) => (
              <GlassCard key={i} style={{ padding: 'var(--space-3) var(--space-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                  <div style={{
                    width: '2rem', height: '2rem', borderRadius: 'var(--radius-full)',
                    background: m.side === 'seller' ? 'var(--color-accent-subtle)' : 'color-mix(in oklch, var(--color-info) 15%, transparent)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 'var(--text-xs)',
                    color: m.side === 'seller' ? 'var(--color-accent)' : 'var(--color-info)',
                  }}>
                    {(m.name || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{m.name}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                      {m.side === 'seller' ? '셀러' : '바이어'}
                    </div>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        </FadeUp>
      )}

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: 'var(--space-8) 0', borderTop: '1px solid var(--color-border-subtle)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)' }}>
          <FontAwesomeIcon icon={faHandshake} style={{ color: 'var(--color-accent)' }} />
          Powered by 딜룸
        </div>
      </div>
    </main>
  );
}
